import { parseProject, type Project } from '../core/schema';

const DB_NAME = 'open-motion-studio';
const DB_VERSION = 1;
const PROJECTS = 'projects';
const ASSETS = 'assets';
const DERIVED = 'derived';
const SNAPSHOTS = 'snapshots';
const SETTINGS = 'settings';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (!('indexedDB' in window)) return Promise.reject(new Error('IndexedDB is not available'));
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PROJECTS)) db.createObjectStore(PROJECTS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(ASSETS)) db.createObjectStore(ASSETS, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(DERIVED)) db.createObjectStore(DERIVED, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(SNAPSHOTS)) {
        const store = db.createObjectStore(SNAPSHOTS, { keyPath: 'key' });
        store.createIndex('projectId', 'projectId');
      }
      if (!db.objectStoreNames.contains(SETTINGS)) db.createObjectStore(SETTINGS, { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(storeName: string, mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase();
  const transaction = db.transaction(storeName, mode);
  return requestResult(operation(transaction.objectStore(storeName)));
}

export async function saveProject(project: Project, createSnapshot = false): Promise<void> {
  const saved = structuredClone({ ...project, updatedAt: Date.now() });
  try {
    await withStore(PROJECTS, 'readwrite', (store) => store.put(saved));
    await setSetting('lastProjectId', project.id);
    if (createSnapshot) await saveSnapshot(saved);
  } catch (error) {
    localStorage.setItem(`om-project:${project.id}`, JSON.stringify(saved));
    localStorage.setItem('om-last-project', project.id);
    if (error instanceof DOMException && error.name === 'QuotaExceededError') throw new Error('Local storage is full. Remove cached proxies or export the project package.');
  }
}

export async function loadProject(id: string): Promise<Project | null> {
  try {
    const value = await withStore<Project | undefined>(PROJECTS, 'readonly', (store) => store.get(id));
    return value ? parseProject(value) : null;
  } catch {
    const value = localStorage.getItem(`om-project:${id}`);
    return value ? parseProject(JSON.parse(value)) : null;
  }
}

export async function loadLastProject(): Promise<Project | null> {
  const id = await getSetting<string>('lastProjectId').catch(() => localStorage.getItem('om-last-project'));
  return id ? loadProject(id) : null;
}

export async function listProjects(): Promise<Project[]> {
  try {
    const projects = await withStore<Project[]>(PROJECTS, 'readonly', (store) => store.getAll());
    return projects.map(parseProject).sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return Object.keys(localStorage)
      .filter((key) => key.startsWith('om-project:'))
      .map((key) => parseProject(JSON.parse(localStorage.getItem(key)!)))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }
}

export async function deleteProject(projectId: string): Promise<void> {
  try { await withStore(PROJECTS, 'readwrite', (store) => store.delete(projectId)); } catch { localStorage.removeItem(`om-project:${projectId}`); }
}

export async function putAssetBlob(projectId: string, assetId: string, blob: Blob): Promise<void> {
  await withStore(ASSETS, 'readwrite', (store) => store.put({ key: `${projectId}:${assetId}`, projectId, assetId, blob, updatedAt: Date.now() }));
}

export async function getAssetBlob(projectId: string, assetId: string): Promise<Blob | null> {
  try {
    const row = await withStore<{ blob: Blob } | undefined>(ASSETS, 'readonly', (store) => store.get(`${projectId}:${assetId}`));
    return row?.blob ?? null;
  } catch { return null; }
}

export async function putDerivedBlob(projectId: string, assetId: string, type: string, blob: Blob): Promise<void> {
  await withStore(DERIVED, 'readwrite', (store) => store.put({ key: `${projectId}:${assetId}:${type}`, projectId, assetId, type, blob, updatedAt: Date.now() }));
}

export async function getDerivedBlob(projectId: string, assetId: string, type: string): Promise<Blob | null> {
  try {
    const row = await withStore<{ blob: Blob } | undefined>(DERIVED, 'readonly', (store) => store.get(`${projectId}:${assetId}:${type}`));
    return row?.blob ?? null;
  } catch { return null; }
}

export async function saveSnapshot(project: Project): Promise<void> {
  const row = { key: `${project.id}:${Date.now()}`, projectId: project.id, createdAt: Date.now(), project };
  await withStore(SNAPSHOTS, 'readwrite', (store) => store.put(row));
  const snapshots = await listSnapshots(project.id);
  for (const old of snapshots.slice(12)) await withStore(SNAPSHOTS, 'readwrite', (store) => store.delete(old.key));
}

export async function listSnapshots(projectId: string): Promise<Array<{ key: string; createdAt: number; project: Project }>> {
  const db = await openDatabase();
  const transaction = db.transaction(SNAPSHOTS, 'readonly');
  const index = transaction.objectStore(SNAPSHOTS).index('projectId');
  const rows = await requestResult<Array<{ key: string; createdAt: number; project: Project }>>(index.getAll(projectId));
  return rows.sort((a, b) => b.createdAt - a.createdAt);
}

export async function setSetting<T>(key: string, value: T): Promise<void> {
  try { await withStore(SETTINGS, 'readwrite', (store) => store.put({ key, value })); }
  catch { localStorage.setItem(`om-setting:${key}`, JSON.stringify(value)); }
}

export async function getSetting<T>(key: string): Promise<T | null> {
  try {
    const row = await withStore<{ value: T } | undefined>(SETTINGS, 'readonly', (store) => store.get(key));
    return row?.value ?? null;
  } catch {
    const value = localStorage.getItem(`om-setting:${key}`);
    return value ? JSON.parse(value) as T : null;
  }
}

export function createDebouncedProjectSaver(delay = 850) {
  let timeout = 0;
  let pending: Project | null = null;
  let lastSnapshot = 0;
  const flush = async () => {
    if (!pending) return;
    const project = pending;
    pending = null;
    const snapshot = Date.now() - lastSnapshot > 5 * 60_000;
    if (snapshot) lastSnapshot = Date.now();
    await saveProject(project, snapshot);
  };
  return {
    schedule(project: Project) {
      pending = project;
      clearTimeout(timeout);
      timeout = window.setTimeout(() => void flush(), delay);
    },
    flush
  };
}
